import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import { useParams, useHistory } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import MPHeader1 from "../MainPageComps/MPHeader1";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Component to handle map center changes
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (
      center &&
      center[0] &&
      center[1] &&
      !isNaN(center[0]) &&
      !isNaN(center[1])
    ) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
}

// Placeholder image component
const PlaceholderImage = ({ type = "main", alt, windowWidth }) => {
  const getPlaceholder = () => {
    switch (type) {
      case "main":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <div style={{ fontSize: "48px" }}>✂️</div>
            <div style={{ fontSize: "14px", color: "#999", fontWeight: "500" }}>
              No Image
            </div>
          </div>
        );
      case "secondary":
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#ececec",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ fontSize: "32px" }}>📷</div>
          </div>
        );
      case "profile":
        return (
          <div
            style={{
              width: windowWidth <= 768 ? "60px" : "70px",
              height: windowWidth <= 768 ? "60px" : "70px",
              borderRadius: "50%",
              backgroundColor: "#007AFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "3px solid #007AFF",
            }}
          >
            <div style={{ fontSize: "32px" }}>👤</div>
          </div>
        );
      default:
        return null;
    }
  };

  return getPlaceholder();
};

export default function AllCategories({ CategoryName }) {
  const params = useParams();
  const history = useHistory();
  const category = CategoryName || params.CategoryName;

  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState({});
  const [barberImages, setBarberImages] = useState({});
  const [barberRatings, setBarberRatings] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBarber, setSelectedBarber] = useState(null);
  //const [mapCenter, setMapCenter] = useState([32.0853, 34.7818]); // Default to Tel Aviv
  const [mapCenter, setMapCenter] = useState([62.0, 15.0]); // Default to Sweden

  const [mapZoom, setMapZoom] = useState(5);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // New state for search and filters
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [userLocation, setUserLocation] = useState(null);
  const [showNearbyOnly, setShowNearbyOnly] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [backHovered, setBackHovered] = useState(false);

  const itemsPerPage = 10;

  // Ref for the list container
  const listContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (category === "haircut") {
      fetchBarbers();
    }
  }, [category]);

  useEffect(() => {
    // Calculate map center when barbers are loaded
    if (barbers.length > 0) {
      const validBarbers = barbers.filter(
        (item) =>
          item.location?.lat &&
          item.location?.long &&
          !isNaN(parseFloat(item.location.lat)) &&
          !isNaN(parseFloat(item.location.long)),
      );

      if (validBarbers.length > 0) {
        const centerLat =
          validBarbers.reduce(
            (sum, item) => sum + parseFloat(item.location.lat),
            0,
          ) / validBarbers.length;
        const centerLng =
          validBarbers.reduce(
            (sum, item) => sum + parseFloat(item.location.long),
            0,
          ) / validBarbers.length;

        if (!isNaN(centerLat) && !isNaN(centerLng)) {
          setMapCenter([centerLat, centerLng]);
        }
      }
    }
  }, [barbers]);

  const handleBack = () => {
    history.goBack();
  };

  const fetchBarbers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${MyDomain}Barbers2/GetBarbersse`);
      console.log(response.data);
      setBarbers(response.data);

      response.data.forEach((item) => {
        if (item.location?.lat && item.location?.long) {
          getAddressFromCoordinates(
            item.barber.id,
            item.location.lat,
            item.location.long,
          );
        }

        fetchBarberImages(item.barber.id);
        fetchBarberReviews(item.barber.phoneNumber);
      });
    } catch (error) {
      console.error("Error fetching barbers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBarberImages = async (barberId) => {
    try {
      const response = await axios.get(
        `${MyDomain}Barbers2/GetAllImagesByBarberIdse/${barberId}`,
      );

      setBarberImages((prev) => ({
        ...prev,
        [barberId]: {
          mainImage: response.data.mainImage,
          mainImage2: response.data.mainImage2,
          mainImage3: response.data.mainImage3,
        },
      }));
    } catch (error) {
      console.error(`Error fetching images for barber ${barberId}:`, error);
    }
  };

  const fetchBarberReviews = async (phoneNumber) => {
    try {
      const response = await axios.post(
        `${MyDomain}Barbers2/GetAllBarberDatase`,
        { phoneNumber: phoneNumber },
      );

      const reviews = response.data.allReviews || [];

      if (reviews.length > 0) {
        const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
        const average = (sum / reviews.length).toFixed(1);

        setBarberRatings((prev) => ({
          ...prev,
          [phoneNumber]: {
            average: average,
            count: reviews.length,
          },
        }));
      }
    } catch (error) {
      console.error(`Error fetching reviews for ${phoneNumber}:`, error);
    }
  };

  const getAddressFromCoordinates = async (barberId, lat, lng) => {
    const apiKey = "AIzaSyA4HcCM2bP0v8Chi2bdFL7vAhCVkepKsBY";
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      if (data.results[0]) {
        const addressComponents = data.results[0].address_components;
        const street =
          addressComponents.find((c) => c.types.includes("route"))?.long_name ||
          "";
        const city =
          addressComponents.find((c) => c.types.includes("locality"))
            ?.long_name || "";

        const address =
          street && city
            ? `${street}, ${city}`
            : data.results[0].formatted_address;

        setAddresses((prev) => ({
          ...prev,
          [barberId]: address,
        }));
      }
    } catch (error) {
      console.error("Error fetching address:", error);
    }
  };

  const renderStars = (rating) => {
    const starSize = windowWidth <= 768 ? 14 : 16;

    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill={star <= rating ? "#FCD34D" : "#E5E7EB"}
            stroke={star <= rating ? "#F59E0B" : "#D1D5DB"}
            strokeWidth="1"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>
    );
  };

  const handleBarberClick = (phoneNumber, countryNumber) => {
    history.push(`/barber-main-page/${phoneNumber}`);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleBarberHover = (barberId, lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);

    if (!isNaN(latitude) && !isNaN(longitude)) {
      setSelectedBarber(barberId);
      setMapCenter([latitude, longitude]);
      setMapZoom(12); // Zoom in when hovering
    }
  };

  const handleBarberLeave = () => {
    setSelectedBarber(null);
    // Zoom out but keep the current center (the last hovered barber's location)
    setMapZoom(8); // Zoom out to a medium level, but keep the center
  };

  // Calculate distance between two coordinates using Haversine formula (in km)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  // Get user's current location
  const handleGetUserLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLat = position.coords.latitude;
        const userLng = position.coords.longitude;

        setUserLocation({ lat: userLat, lng: userLng });
        setShowNearbyOnly(true);
        setMapCenter([userLat, userLng]);
        setMapZoom(13);
        setLocationLoading(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        let errorMessage = "Unable to get your location. ";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage +=
              "Please allow location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out.";
            break;
          default:
            errorMessage += "An unknown error occurred.";
        }

        alert(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  // Clear location filter
  const handleClearLocation = () => {
    setUserLocation(null);
    setShowNearbyOnly(false);

    // Reset map to show all barbers
    const validBarbers = barbers.filter(
      (item) =>
        item.location?.lat &&
        item.location?.long &&
        !isNaN(parseFloat(item.location.lat)) &&
        !isNaN(parseFloat(item.location.long)),
    );

    if (validBarbers.length > 0) {
      const centerLat =
        validBarbers.reduce(
          (sum, item) => sum + parseFloat(item.location.lat),
          0,
        ) / validBarbers.length;
      const centerLng =
        validBarbers.reduce(
          (sum, item) => sum + parseFloat(item.location.long),
          0,
        ) / validBarbers.length;

      if (!isNaN(centerLat) && !isNaN(centerLng)) {
        setMapCenter([centerLat, centerLng]);
        setMapZoom(12);
      }
    }
  };

  // Filter and search logic
  const getFilteredBarbers = () => {
    let filtered = barbers;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((item) => {
        const name = item.barber.name?.toLowerCase() || "";
        const phone = item.barber.phoneNumber?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        return name.includes(search) || phone.includes(search);
      });
    }

    // Rating filter
    if (ratingFilter !== "all") {
      filtered = filtered.filter((item) => {
        const rating = barberRatings[item.barber.phoneNumber];
        if (!rating) return false;

        const avgRating = parseFloat(rating.average);

        switch (ratingFilter) {
          case "5":
            return avgRating >= 4.5;
          case "4":
            return avgRating >= 4 && avgRating < 4.5;
          case "3":
            return avgRating >= 3 && avgRating < 4;
          case "below3":
            return avgRating < 3;
          default:
            return true;
        }
      });
    }

    // Location filter (within 15km)
    if (showNearbyOnly && userLocation) {
      filtered = filtered.filter((item) => {
        if (!item.location?.lat || !item.location?.long) return false;

        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          parseFloat(item.location.lat),
          parseFloat(item.location.long),
        );

        return distance <= 15; // 15km radius
      });
    }

    return filtered;
  };

  const filteredBarbers = getFilteredBarbers();

  // Pagination calculations
  const totalPages = Math.ceil(filteredBarbers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBarbers = filteredBarbers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, ratingFilter, showNearbyOnly]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);

    // Scroll to top of the list container on mobile, or smooth scroll on desktop
    if (windowWidth <= 768) {
      // On mobile, scroll the window to the top of the list
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      // On desktop, scroll within the list container
      if (listContainerRef.current) {
        listContainerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Get valid barbers with coordinates for map
  const validBarbersForMap = filteredBarbers.filter(
    (item) =>
      item.location?.lat &&
      item.location?.long &&
      !isNaN(parseFloat(item.location.lat)) &&
      !isNaN(parseFloat(item.location.long)),
  );

  // Determine if map should be shown
  const showMap = windowWidth > 1024;

  return (
    <div
      style={{
        paddingLeft:
          windowWidth <= 1024
            ? "0%"
            : windowWidth <= 1920
              ? "10%"
              : windowWidth <= 2560
                ? "20%"
                : windowWidth <= 3840
                  ? "25%"
                  : "30%",
        paddingRight:
          windowWidth <= 1024
            ? "0%"
            : windowWidth <= 1920
              ? "10%"
              : windowWidth <= 2560
                ? "20%"
                : windowWidth <= 3840
                  ? "25%"
                  : "30%",
        justifyContent: "center",
        fontFamily: "Droid Arabic Kufi, sans-serif",
      }}
    >
      {windowWidth <= 1024 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <button
            onClick={handleBack}
            onMouseEnter={() => setBackHovered(true)}
            onMouseLeave={() => setBackHovered(false)}
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: backHovered
                ? "rgba(0, 0, 0, 0.08)"
                : "rgba(0, 0, 0, 0.04)",
              border: "none",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              flexShrink: 0,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#333"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <div style={{ flex: 1, textAlign: "center" }}>
            <h2
              style={{
                margin: 0,
                fontSize: "18px",
                fontWeight: "700",
                color: "#333",
                textTransform: "capitalize",
              }}
            >
              {category} Salons
            </h2>
          </div>

          <div style={{ width: "40px" }}></div>
        </div>
      )}

      {windowWidth > 1024 && <MPHeader1 />}

      <div style={{ padding: windowWidth <= 768 ? "10px" : "20px" }}>
        {category === "haircut" && (
          <div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  minHeight: "400px",
                }}
              >
                {/* Loading Spinner */}
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #007AFF",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "24px",
                  }}
                />

                {/* Loading Text */}
                <h3
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Loading Salons
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  Please wait while we find the best barbers for you...
                </p>

                {/* CSS Animation */}
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
              </div>
            ) : (
              <>
                {/* Search and Filter Bar */}
                <div
                  style={{
                    maxWidth: showMap ? "48%" : "00%",
                    margin: "0 0 20px 0",
                    display: "flex",
                    gap: "15px",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {/* Search Input */}
                  <div style={{ flex: "1 1 300px", position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Search by name or phone number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 40px 12px 16px",
                        fontSize: "14px",
                        border: "1px solid #e0e0e0",
                        outline: "none",
                        transition: "border-color 0.3s",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#007AFF")}
                      onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "18px",
                        color: "#999",
                      }}
                    >
                      🔍
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      flexDirection: windowWidth <= 768 ? "column" : "row",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: windowWidth <= 768 ? "10px" : "0",
                    }}
                  >
                    {/* Rating Filter */}
                    <div
                      style={{
                        flex: "0 0 auto",
                        width: windowWidth <= 768 ? "100%" : "auto",
                      }}
                    >
                      <select
                        value={ratingFilter}
                        onChange={(e) => setRatingFilter(e.target.value)}
                        style={{
                          padding: "10px 14px",
                          fontSize: "14px",
                          border: "1px solid #e0e0e0",
                          outline: "none",
                          cursor: "pointer",
                          backgroundColor: "white",
                          minWidth: windowWidth <= 768 ? "100%" : "150px",
                          width: windowWidth <= 768 ? "100%" : "auto",
                        }}
                      >
                        <option value="all">All Ratings</option>
                        <option value="5">⭐ 4.5 - 5.0</option>
                        <option value="4">⭐ 4.0 - 4.4</option>
                        <option value="3">⭐ 3.0 - 3.9</option>
                        <option value="below3">⭐ Below 3.0</option>
                      </select>
                    </div>

                    {/* Closest to Me Button */}
                    {!showNearbyOnly ? (
                      <button
                        onClick={handleGetUserLocation}
                        disabled={locationLoading}
                        style={{
                          padding: "12px 20px",
                          fontSize: "14px",
                          backgroundColor: locationLoading
                            ? "#e0e0e0"
                            : "#18181b",
                          color: "white",
                          border: "1px solid black",
                          cursor: locationLoading ? "not-allowed" : "pointer",
                          transition: "background-color 0.3s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          whiteSpace: "nowrap",
                          width: windowWidth <= 768 ? "100%" : "auto",
                        }}
                        onMouseEnter={(e) => {
                          if (!locationLoading) {
                            e.target.style.backgroundColor = "black";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!locationLoading) {
                            e.target.style.backgroundColor = "#18181b";
                          }
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>📍</span>
                        {locationLoading
                          ? "Getting Location..."
                          : "Close to Me"}
                      </button>
                    ) : (
                      <button
                        onClick={handleClearLocation}
                        style={{
                          padding: "12px 20px",
                          fontSize: "14px",
                          backgroundColor: "#18181b",
                          color: "white",
                          border: "1px solid black",
                          cursor: "pointer",
                          transition: "background-color 0.3s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          whiteSpace: "nowrap",
                          width: windowWidth <= 768 ? "100%" : "auto",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "black")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#18181b")
                        }
                      >
                        <span style={{ fontSize: "16px" }}>📍</span>
                        Within 15km ✓
                      </button>
                    )}
                  </div>
                  <p
                    style={{
                      color: "#666",
                      textAlign: "center",
                      fontSize: 12,
                      width: "100%",
                      margin: 0,
                    }}
                  >
                    Showing more than {filteredBarbers.length - 1} salons for{" "}
                    <span style={{ textTransform: "capitalize" }}>
                      {category}
                    </span>
                  </p>
                  {/* Clear Filters Button */}
                  {(searchTerm || ratingFilter !== "all" || showNearbyOnly) && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setRatingFilter("all");
                        if (showNearbyOnly) {
                          handleClearLocation();
                        }
                      }}
                      style={{
                        padding: "12px 20px",
                        fontSize: "14px",
                        backgroundColor: "#f5f5f5",
                        color: "#666",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontWeight: "600",
                        transition: "background-color 0.3s",
                        width: windowWidth <= 768 ? "100%" : "auto",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#e0e0e0")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#f5f5f5")
                      }
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>

                {/* Split Screen Layout or Full Width */}
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    maxWidth: "100%",
                    margin: "0 auto",
                  }}
                >
                  {/* Left Side - List (One per row) */}
                  <div
                    style={{
                      flex: showMap ? "0 0 48%" : "1",
                      display: "flex",
                      flexDirection: "column",
                      maxHeight:
                        windowWidth <= 768 ? "none" : "calc(100vh - 280px)",
                    }}
                  >
                    {/* Scrollable content area */}
                    <div
                      ref={listContainerRef}
                      style={{
                        flex: 1,
                        overflowY: windowWidth <= 768 ? "visible" : "auto",
                        paddingRight: windowWidth <= 768 ? "0" : "10px",
                      }}
                    >
                      {currentBarbers.length === 0 ? (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "60px 20px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "12px",
                          }}
                        >
                          <p style={{ fontSize: "18px", color: "#666" }}>
                            No barbers found matching your criteria
                          </p>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#999",
                              marginTop: "10px",
                            }}
                          >
                            Try adjusting your search or filters
                          </p>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          {currentBarbers.map((item, index) => {
                            const images = barberImages[item.barber.id] || {};
                            const hasMainImg =
                              images.mainImage?.image || item.barber.image;
                            const hasImg2 = images.mainImage2?.image;
                            const hasImg3 = images.mainImage3?.image;
                            const hasProfileImg = item.barber.image;
                            const rating =
                              barberRatings[item.barber.phoneNumber];

                            return (
                              <div
                                key={item.barber.id || index}
                                onMouseEnter={() => {
                                  if (
                                    showMap &&
                                    item.location?.lat &&
                                    item.location?.long
                                  ) {
                                    handleBarberHover(
                                      item.barber.id,
                                      item.location.lat,
                                      item.location.long,
                                    );
                                  }
                                }}
                                onMouseLeave={() =>
                                  showMap && handleBarberLeave()
                                }
                                onClick={() =>
                                  handleBarberClick(
                                    item.barber.phoneNumber,
                                    item.barber.countryNumber,
                                  )
                                }
                                style={{
                                  backgroundColor: "white",
                                  borderRadius: "5px",
                                  overflow: "hidden",
                                  boxShadow:
                                    selectedBarber === item.barber.id
                                      ? "0 8px 20px rgba(0, 0, 0, 0.3)"
                                      : "0 4px 6px rgba(0, 0, 0, 0.1)",
                                  transition: "all 0.3s ease",
                                  cursor: "pointer",
                                  border:
                                    selectedBarber === item.barber.id
                                      ? "2px solid #000000"
                                      : "2px solid transparent",
                                  display: "flex",
                                  flexDirection:
                                    windowWidth <= 768 ? "column" : "row",
                                  height: windowWidth <= 768 ? "auto" : "280px",
                                  minHeight:
                                    windowWidth <= 768 ? "450px" : "280px",
                                }}
                              >
                                {/* Left - Image Grid (1 large top, 2 small bottom) */}
                                <div
                                  style={{
                                    flex:
                                      windowWidth <= 768
                                        ? "0 0 250px"
                                        : "0 0 45%",
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gridTemplateRows: "60% 40%",
                                    gap: "4px",
                                    backgroundColor: "#f5f5f5",
                                  }}
                                >
                                  {/* Main Image - Spans both columns on top */}
                                  <div
                                    style={{
                                      gridColumn: "1 / 3",
                                      gridRow: "1",
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {hasMainImg ? (
                                      <img
                                        src={hasMainImg}
                                        alt={item.barber.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <PlaceholderImage
                                        type="main"
                                        alt={item.barber.name}
                                        windowWidth={windowWidth}
                                      />
                                    )}
                                  </div>

                                  {/* Image 2 - Bottom left */}
                                  <div
                                    style={{
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {hasImg2 ? (
                                      <img
                                        src={hasImg2}
                                        alt={`${item.barber.name} - Image 2`}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <PlaceholderImage
                                        type="secondary"
                                        alt="Image 2"
                                        windowWidth={windowWidth}
                                      />
                                    )}
                                  </div>

                                  {/* Image 3 - Bottom right */}
                                  <div
                                    style={{
                                      position: "relative",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {hasImg3 ? (
                                      <img
                                        src={hasImg3}
                                        alt={`${item.barber.name} - Image 3`}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <PlaceholderImage
                                        type="secondary"
                                        alt="Image 3"
                                        windowWidth={windowWidth}
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Right - Barber Info */}
                                <div
                                  style={{
                                    flex: 1,
                                    padding:
                                      windowWidth <= 768 ? "16px" : "24px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  {/* Top Section - Profile */}
                                  <div>
                                    {/* Profile Icon + Name - Now in Column for Mobile */}
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection:
                                          windowWidth <= 768 ? "column" : "row",
                                        alignItems:
                                          windowWidth <= 768
                                            ? "center"
                                            : "center",
                                        gap:
                                          windowWidth <= 768 ? "10px" : "14px",
                                        marginBottom: "16px",
                                      }}
                                    >
                                      {hasProfileImg ? (
                                        <img
                                          src={item.barber.image}
                                          alt={item.barber.name}
                                          style={{
                                            width:
                                              windowWidth <= 768
                                                ? "60px"
                                                : "70px",
                                            height:
                                              windowWidth <= 768
                                                ? "60px"
                                                : "70px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "3px solid #007AFF",
                                            flexShrink: 0,
                                          }}
                                        />
                                      ) : (
                                        <PlaceholderImage
                                          type="profile"
                                          alt={item.barber.name}
                                          windowWidth={windowWidth}
                                        />
                                      )}

                                      <div
                                        style={{
                                          flex: 1,
                                          minWidth: 0,
                                          width:
                                            windowWidth <= 768
                                              ? "100%"
                                              : "auto",
                                          textAlign:
                                            windowWidth <= 768
                                              ? "center"
                                              : "left",
                                        }}
                                      >
                                        <h3
                                          style={{
                                            margin: "0 0 8px 0",
                                            fontSize:
                                              windowWidth <= 768
                                                ? "16px"
                                                : "20px",
                                            fontWeight: "700",
                                            color: "#333",
                                            whiteSpace:
                                              windowWidth <= 768
                                                ? "normal"
                                                : "nowrap",
                                            overflow:
                                              windowWidth <= 768
                                                ? "visible"
                                                : "hidden",
                                            textOverflow:
                                              windowWidth <= 768
                                                ? "clip"
                                                : "ellipsis",
                                            wordBreak:
                                              windowWidth <= 768
                                                ? "break-word"
                                                : "normal",
                                          }}
                                        >
                                          {item.barber.name}
                                        </h3>

                                        {/* Phone Number */}
                                        <p
                                          style={{
                                            margin: "0",
                                            fontSize:
                                              windowWidth <= 768
                                                ? "12px"
                                                : "13px",
                                            color: "#999",
                                          }}
                                        >
                                          📱 {item.barber.phoneNumber}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Address */}
                                    {addresses[item.barber.id] && (
                                      <p
                                        style={{
                                          margin: "0 0 16px 0",
                                          fontSize:
                                            windowWidth <= 768
                                              ? "12px"
                                              : "14px",
                                          color: "#666",
                                          display: "flex",
                                          alignItems: "flex-start",
                                          gap: "8px",
                                          lineHeight: "1.5",
                                          textAlign:
                                            windowWidth <= 768
                                              ? "center"
                                              : "left",
                                          justifyContent:
                                            windowWidth <= 768
                                              ? "center"
                                              : "flex-start",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize:
                                              windowWidth <= 768
                                                ? "14px"
                                                : "16px",
                                          }}
                                        >
                                          📍
                                        </span>
                                        <span>{addresses[item.barber.id]}</span>
                                      </p>
                                    )}
                                  </div>

                                  {/* Bottom Section - Rating */}
                                  {rating && (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: windowWidth <= 768 ? "4px" : "8px",
                                        padding:
                                          windowWidth <= 768
                                            ? "6px 8px"
                                            : "8px 12px",
                                        backgroundColor: "#f8f9fa",
                                        borderRadius:
                                          windowWidth <= 768 ? "6px" : "8px",
                                        border: "1px solid #e9ecef",
                                        flexWrap: "wrap",
                                        justifyContent:
                                          windowWidth <= 768
                                            ? "center"
                                            : "flex-start",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap:
                                            windowWidth <= 768 ? "2px" : "4px",
                                        }}
                                      >
                                        {renderStars(
                                          Math.round(
                                            parseFloat(rating.average),
                                          ),
                                        )}
                                        <span
                                          style={{
                                            fontSize:
                                              windowWidth <= 768
                                                ? "11px"
                                                : "12px",
                                            fontWeight: "600",
                                            color: "#333",
                                            marginLeft:
                                              windowWidth <= 768 ? "2px" : "0",
                                          }}
                                        >
                                          {rating.average}
                                        </span>
                                      </div>
                                      <span
                                        style={{
                                          fontSize:
                                            windowWidth <= 768
                                              ? "10px"
                                              : "12px",
                                          color: "#666",
                                          fontWeight: "500",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        ({rating.count}{" "}
                                        {rating.count === 1
                                          ? "review"
                                          : "reviews"}
                                        )
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {/* Sticky Pagination at bottom */}
                    {totalPages > 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "10px",
                          padding: "15px 10px",
                          backgroundColor: "white",
                          borderTop: "1px solid #e0e0e0",
                          position: "sticky",
                          bottom: 0,
                          zIndex: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={handlePrevious}
                          disabled={currentPage === 1}
                          style={{
                            padding: "8px 16px",
                            backgroundColor:
                              currentPage === 1 ? "#e0e0e0" : "#18181b",
                            color: currentPage === 1 ? "#999" : "white",
                            border: "none",
                            cursor:
                              currentPage === 1 ? "not-allowed" : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Previous
                        </button>

                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          {Array.from(
                            {
                              length: Math.min(
                                totalPages,
                                windowWidth <= 768 ? 5 : 10,
                              ),
                            },
                            (_, i) => {
                              let pageNum;
                              const maxPages = windowWidth <= 768 ? 5 : 10;
                              if (totalPages <= maxPages) {
                                pageNum = i + 1;
                              } else if (
                                currentPage <= Math.floor(maxPages / 2)
                              ) {
                                pageNum = i + 1;
                              } else if (
                                currentPage >=
                                totalPages - Math.floor(maxPages / 2)
                              ) {
                                pageNum = totalPages - maxPages + i + 1;
                              } else {
                                pageNum =
                                  currentPage - Math.floor(maxPages / 2) + i;
                              }

                              return (
                                <button
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    padding: "8px 12px",
                                    backgroundColor:
                                      currentPage === pageNum
                                        ? "#18181b"
                                        : "white",
                                    color:
                                      currentPage === pageNum
                                        ? "white"
                                        : "#333",
                                    border: "1px solid #ddd",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            },
                          )}
                        </div>

                        <button
                          onClick={handleNext}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: "8px 16px",
                            backgroundColor:
                              currentPage === totalPages
                                ? "#e0e0e0"
                                : "#18181b",
                            color:
                              currentPage === totalPages ? "#999" : "white",
                            border: "none",
                            cursor:
                              currentPage === totalPages
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "12px",
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Map (only shown if windowWidth > 1024) */}
                  {showMap && (
                    <div
                      style={{
                        flex: "0 0 50%",
                        position: "sticky",
                        top: "20px",
                        height: "calc(100vh - 300px)",
                      }}
                    >
                      <MapContainer
                        center={mapCenter}
                        zoom={mapZoom}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "12px",
                          overflow: "hidden",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        }}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapUpdater center={mapCenter} zoom={mapZoom} />

                        {/* User Location Marker */}
                        {userLocation && showNearbyOnly && (
                          <Marker
                            position={[userLocation.lat, userLocation.lng]}
                            icon={L.divIcon({
                              className: "custom-user-marker",
                              html: `<div style="
                                background-color: #007AFF;
                                width: 20px;
                                height: 20px;
                                border-radius: 50%;
                                border: 3px solid white;
                                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                              "></div>`,
                              iconSize: [20, 20],
                              iconAnchor: [10, 10],
                            })}
                          >
                            <Popup>
                              <div style={{ textAlign: "center" }}>
                                <strong>📍 Your Location</strong>
                                <p
                                  style={{
                                    margin: "5px 0 0 0",
                                    fontSize: "12px",
                                  }}
                                >
                                  Showing barbers within 15km
                                </p>
                              </div>
                            </Popup>
                          </Marker>
                        )}

                        {validBarbersForMap.map((item, index) => (
                          <Marker
                            key={item.barber.id || index}
                            position={[
                              parseFloat(item.location.lat),
                              parseFloat(item.location.long),
                            ]}
                          >
                            <Popup>
                              <div style={{ textAlign: "center" }}>
                                <strong>{item.barber.name}</strong>
                                {addresses[item.barber.id] && (
                                  <p
                                    style={{
                                      margin: "5px 0 0 0",
                                      fontSize: "12px",
                                    }}
                                  >
                                    📍 {addresses[item.barber.id]}
                                  </p>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
